import { type ApolloError } from '@apollo/client';
import {
  Button,
  Divider,
  ErrorState,
  Flex,
  FormRadioSelection,
  FormTextField,
  Grid,
  Label,
  Loading,
  ReadonlyField,
  Text,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useHasPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { formatDateTime } from '@shared/utils';
import {
  useUnlockUserTwoFactorMutation,
  useResetUserTwoFactorMutation,
  GetUserMetadataDocument,
} from '@user-management/graphql';
import { APP_PERMISSIONS } from '@user-management/permissions';
import { UserAppRoleSelection } from '@user-management/role-management';
import { ResendEmailInvitationButton } from '@user-management/user-invitation';

import { GridRow, UserInformation } from '../../components';
import { UserStatus, TwoFAStatus, TWO_FA_COLOR_STATUS, TWO_FA_STATUS_TEXT } from '../../constants';
import { type UserDetailsDisabledFields, type User } from '../../types';
import { TwoFaActionModal } from '../two-fa-action-modal';

interface UserDetailsFormModalContentProps {
  loading: boolean;
  error: ApolloError | undefined;
  data: User | undefined;
  disabledFields?: UserDetailsDisabledFields;
}

export default function UserDetailsFormModalContent(
  props: Readonly<UserDetailsFormModalContentProps>,
) {
  const { loading: isLoading, error, data: user, disabledFields } = props;

  const { showSnackbar } = useSnackbar();
  const [unlockUserTwoFactor] = useUnlockUserTwoFactorMutation();
  const [resetUserTwoFactor] = useResetUserTwoFactorMutation();

  const [isTwoFaUnlockModalOpen, { open: openTwoFaUnlockModal, close: closeTwoFaUnlockModal }] =
    useDisclosure(false);
  const [isTwoFaResetModalOpen, { open: openTwoFaResetModal, close: closeTwoFaResetModal }] =
    useDisclosure(false);
  const hasEditStatusPermission = useHasPermission(APP_PERMISSIONS.SETTING.USER.STATUS);

  if (isLoading) {
    return <Loading />;
  } else if (error || !user) {
    return <ErrorState />;
  }

  const handleUnlockConfirm = async (reason: string) => {
    try {
      const { data } = await unlockUserTwoFactor({
        variables: { data: { userId: user.id, reason } },
        refetchQueries: [{ query: GetUserMetadataDocument, variables: { userId: user.id } }],
        awaitRefetchQueries: true,
      });
      if (data?.suUnlockUserTwoFactor.success) {
        showSnackbar({ message: '2FA unlocked successfully', variant: 'success' });
      } else {
        showSnackbar({
          message: '2FA unlocked failed',
          variant: 'error',
        });
      }
    } catch {
      showSnackbar({
        message: '2FA unlocked failed',
        variant: 'error',
      });
    }
  };

  const handleResetConfirm = async (reason: string) => {
    try {
      const { data } = await resetUserTwoFactor({
        variables: { data: { userId: user.id, reason } },
        refetchQueries: [{ query: GetUserMetadataDocument, variables: { userId: user.id } }],
        awaitRefetchQueries: true,
      });
      if (data?.suResetUserTwoFactor.success) {
        showSnackbar({ message: '2FA reset successfully', variant: 'success' });
      } else {
        showSnackbar({
          message: '2FA reset failed',
          variant: 'error',
        });
      }
    } catch {
      showSnackbar({
        message: '2FA reset failed',
        variant: 'error',
      });
    }
  };

  const shouldShowInvitationButton = [
    UserStatus.INVITATION_EXPIRED,
    UserStatus.INVITATION_PENDING,
  ].includes(user.status);

  const twoFaStatus = user.twoFactorStatus;

  const shouldShowUnlockButton = twoFaStatus === TwoFAStatus.LOCKED && hasEditStatusPermission;
  const shouldShowResetButton =
    (twoFaStatus === TwoFAStatus.LINKED || twoFaStatus === TwoFAStatus.LOCKED) &&
    hasEditStatusPermission;

  return (
    <>
      <UserInformation
        {...user}
        endAdornment={
          shouldShowInvitationButton ? <ResendEmailInvitationButton userId={user.id} /> : null
        }
      />
      <Divider />
      <GridRow label='ข้อมูลส่วนตัว'>
        <Grid sx={{ gap: (theme) => theme.spacing(6), gridTemplateColumns: '1fr 1fr' }}>
          {disabledFields?.firstName ? (
            <ReadonlyField label='ชื่อภาษาอังกฤษ' content={user.firstName} />
          ) : (
            <FormTextField
              name='firstName'
              placeholder='กรุณากรอกชื่อภาษาอังกฤษ'
              label='ชื่อภาษาอังกฤษ'
            />
          )}
          {disabledFields?.lastName ? (
            <ReadonlyField label='นามสกุลภาษาอังกฤษ' content={user.lastName} />
          ) : (
            <FormTextField
              name='lastName'
              placeholder='กรุณากรอกนามสกุลภาษาอังกฤษ'
              label='นามสกุลภาษาอังกฤษ'
            />
          )}
          {disabledFields?.email ? (
            <ReadonlyField
              label='อีเมลผู้ใช้'
              content={user.email}
              sx={{ gridColumn: 'span 2/span 2' }}
            />
          ) : (
            <FormTextField
              name='email'
              placeholder='กรุณากรอกอีเมลผู้ใช้'
              label='อีเมลผู้ใช้'
              sx={{ gridColumn: 'span 2/span 2' }}
            />
          )}
        </Grid>
      </GridRow>
      <GridRow label='บทบาทและสถานะผู้ใช้'>
        <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(6) }}>
          <UserAppRoleSelection
            name='appRoles'
            label='บทบาทผู้ใช้ในระบบ'
            filterOptions={(v, state) => {
              return v.filter(
                ({ value }) =>
                  typeof value === 'string' &&
                  !user.appRoles?.includes(value.toString()) &&
                  value?.toString().includes(state.inputValue),
              );
            }}
            disabled={disabledFields?.appRoles}
          />
          <FormRadioSelection
            name='activated'
            label='สถานะผู้ใช้'
            options={[
              { label: 'Active', value: 'true' },
              { label: 'Inactive', value: 'false' },
            ]}
            row
            disabled={disabledFields?.activated}
          />
        </Flex>
      </GridRow>
      <GridRow label='ข้อมูลการใช้งาน'>
        <Flex sx={{ gap: (theme) => theme.spacing(6) }}>
          <Flex sx={{ gap: (theme) => theme.spacing(1), flexDirection: 'column' }}>
            <Text variant='body2Semibold'>วันที่ลงทะเบียน</Text>
            <Text>{user.createdAt ? formatDateTime(new Date(user.createdAt)) : '-'}</Text>
          </Flex>
          <Flex sx={{ gap: (theme) => theme.spacing(1), flexDirection: 'column' }}>
            <Text variant='body2Semibold'>วันที่เข้าสู่ระบบล่าสุด</Text>
            <Text>{user.lastLoginAt ? formatDateTime(new Date(user.lastLoginAt)) : '-'}</Text>
          </Flex>
        </Flex>
      </GridRow>
      <GridRow label='สถานะ 2-Factor Authentication'>
        <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Label color={TWO_FA_COLOR_STATUS[twoFaStatus]} variant='outlined'>
            {TWO_FA_STATUS_TEXT[twoFaStatus]}
          </Label>
          {shouldShowUnlockButton || shouldShowResetButton ? (
            <Flex sx={{ gap: (theme) => theme.spacing(2) }}>
              {shouldShowUnlockButton ? (
                <Button
                  variant='outlined'
                  color='secondary'
                  size='small'
                  onClick={openTwoFaUnlockModal}
                >
                  Unlock
                </Button>
              ) : null}
              {shouldShowResetButton ? (
                <Button
                  variant='outlined'
                  color='secondary'
                  size='small'
                  onClick={openTwoFaResetModal}
                >
                  Reset 2FA
                </Button>
              ) : null}
            </Flex>
          ) : null}
        </Flex>
      </GridRow>
      <TwoFaActionModal
        variant='unlock'
        open={isTwoFaUnlockModalOpen}
        onClose={closeTwoFaUnlockModal}
        onConfirm={handleUnlockConfirm}
      />
      <TwoFaActionModal
        variant='reset'
        open={isTwoFaResetModalOpen}
        onClose={closeTwoFaResetModal}
        onConfirm={handleResetConfirm}
      />
    </>
  );
}
